import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
  ViewEncapsulation,
} from '@angular/core';
import { UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { ContractModel } from '../../../../../../../core/models/contract.model';
import { ENUM_PERMISSIONS } from '../../../../../../../core/enums/permission.enum';
import { enableControlWhenAllowed, lockFormWhenReadOnly } from 'src/app/core/utils/form-permission.util';
import { ContractValidityEnum } from '../../../../../../../core/enums/contract.enum';
import { ContractStatusEnum } from '../../../../../../../core/enums/contract-status.enum';
import { TimeUnit } from '../../../../../../../core/utils/time-utils';
import { Observable, Subscription, tap } from 'rxjs';
import { GenericEngagementModel } from '../../../../../../../core/models/generic-engagement.model';
import { UserProfileModel } from '../../../../../../../core/models/user-profile.model';
import { scaleIn400ms } from '../../../../../../../../@vex/animations/scale-in.animation';
import { fadeInRight400ms } from '../../../../../../../../@vex/animations/fade-in-right.animation';
import { stagger40ms } from '../../../../../../../../@vex/animations/stagger.animation';
import { fadeInUp400ms } from '../../../../../../../../@vex/animations/fade-in-up.animation';
import { scaleFadeIn400ms } from '../../../../../../../../@vex/animations/scale-fade-in.animation';
import moment from 'moment/moment';
import { debounceTime, distinctUntilChanged, filter, map, pairwise, startWith } from 'rxjs/operators';
import { ContractService } from '../../../contract.service';
import { UserProfileService } from '../../../../user-profile/user-profile.service';
import { Router } from '@angular/router';
import { AuthService } from '../../../../../../../core/services/auth.service';
import { SnackbarService } from '../../../../../../../core/services/snackbar.service';

@Component({
  selector: 'vex-contract-internal-details-resume',
  templateUrl: './contract-internal-details-resume.component.html',
  styleUrls: ['./contract-internal-details-resume.component.scss'],
  animations: [scaleIn400ms, fadeInRight400ms, stagger40ms, fadeInUp400ms, scaleFadeIn400ms],
  encapsulation: ViewEncapsulation.None,
})
export class ContractInternalDetailsResumeComponent implements OnInit, OnDestroy, OnChanges {
  @Input() contract: ContractModel;
  @Input() files: any[];
  @Input() isUpdateDisabledPermission: boolean;
  @Input() genericEngagement$: Observable<GenericEngagementModel[]>;
  @Input() engagementContacts$: Observable<UserProfileModel[]>;

  @Output() switchToUpdateMode = new EventEmitter<void>();
  @Output() addDenunciationDate = new EventEmitter<void>();
  @Output() genericEngagementAutoCompleteChange = new EventEmitter<any>();
  @Output() engagementContactsAutoCompleteChange = new EventEmitter<any>();
  @Output() dateChanged = new EventEmitter<any>();

  form: UntypedFormGroup;
  ContractValidityEnum = ContractValidityEnum;
  ContractStatusEnum = ContractStatusEnum;
  ENUM_PERMISSIONS = ENUM_PERMISSIONS;
  hasGenericEngagement: boolean;
  isContractArchived: boolean;
  private subscription = new Subscription();

  constructor(
    public contractService: ContractService,
    private userProfileService: UserProfileService,
    private router: Router,
    public authService: AuthService,
    private snackBarService: SnackbarService,
  ) {}

  ngOnInit(): void {
    this.hasGenericEngagement =
      this.contract?.genericEngagement !== null && this.contract?.genericEngagement !== undefined;
    this.isContractArchived = this.contract?.isArchive === true;

    // 1. Initialiser le formulaire (sans souscriptions valueChanges)
    this.initForm();

    // 2. Configurer les souscriptions valueChanges APRÈS l'initialisation
    this.setupValueChangesSubscriptions();

    // 3. Configurer la souscription pour auto-save (pas pour contrats archivés)
    this.initValueChangesSubscription();
    // PRED-3230 : lecture seule intégrale sans droit de modification.
    lockFormWhenReadOnly(this.form, this.isUpdateDisabledPermission);
  }

  initForm(): void {
    this.form = new UntypedFormGroup({
      _id: new UntypedFormControl(),
      genericEngagement: new UntypedFormControl(
        { value: null, disabled: this.isContractArchived },
        Validators.required,
      ),
      supportEstablishment: new UntypedFormControl({ value: null, disabled: true }, Validators.required),
      object: new UntypedFormControl({ value: null, disabled: this.isContractArchived }, Validators.required),
      startValidityDate: new UntypedFormControl({ value: null }),
      endValidityDate: new UntypedFormControl({ value: null, disabled: true }),
      engagementContact: new UntypedFormControl({ value: null }),
    });

    this.form.patchValue(
      {
        _id: this.contract._id,
        genericEngagement: this.contract.genericEngagement,
        supportEstablishment: this.contract.supportEstablishment,
        object: this.contract.object,
        startValidityDate: this.contract.startValidityDate,
        endValidityDate: this.contract.endValidityDate,
        engagementContact: this.contract.engagementContact,
      },
      { emitEvent: false },
    );

    if (this.contract?.supplierContractReference && !this.isContractArchived) {
      enableControlWhenAllowed(this.form, 'supportEstablishment', this.isUpdateDisabledPermission);
    }
    if (this.contract?.startValidityDate) {
      enableControlWhenAllowed(this.form, 'endValidityDate', this.isUpdateDisabledPermission);
    }

    // Activer les champs de dates si l'utilisateur est le contact d'engagement ou Predik
    // et que le contrat n'est pas archivé
    console.log('[initForm] isUserCurrentEngagementContact:', this.isUserCurrentEngagementContact);
    console.log('[initForm] isContractArchived:', this.isContractArchived);
    console.log('[initForm] creationStep:', this.contract?.creationStep);

    if (this.isUserCurrentEngagementContact) {
      console.log('[initForm] Activation des champs de dates');
      enableControlWhenAllowed(this.form, 'startValidityDate', this.isUpdateDisabledPermission);
      if (this.contract?.startValidityDate) {
        enableControlWhenAllowed(this.form, 'endValidityDate', this.isUpdateDisabledPermission);
      }
    }

    console.log('[initForm] startValidityDate disabled:', this.form.controls.startValidityDate.disabled);
    console.log('[initForm] endValidityDate disabled:', this.form.controls.endValidityDate.disabled);
  }

  private setupValueChangesSubscriptions(): void {
    this.subscription.add(
      this.form.controls.startValidityDate.valueChanges.subscribe((date: Date) => {
        if (date) {
          enableControlWhenAllowed(this.form, 'endValidityDate', this.isUpdateDisabledPermission);
        } else {
          this.form.controls.endValidityDate.setValue(null, { emitEvent: false });
          this.form.controls.endValidityDate.disable();
        }
      }),
    );

    this.subscription.add(
      this.form.controls.endValidityDate.valueChanges.subscribe((date: Date) => {
        const startValidityDate = this.form.controls.startValidityDate.value;
        const startDate = moment(startValidityDate).startOf('day').toDate();
        const endDate = moment(date).startOf('day').toDate();

        // Validation des dates uniquement, pas d'appel API ici
        if (date && endDate < startDate) {
          this.form.controls.endValidityDate.setErrors({ invalidDate: true });
        } else {
          this.form.controls.endValidityDate.setErrors(null);
        }
      }),
    );

    this.subscription.add(
      this.form.controls.genericEngagement.valueChanges.subscribe(value => {
        this.engagementContacts$ = this.userProfileService
          .findAll(null, null, null, null, '', {
            hasOnlyInternContactForEstablishment: this.form.controls.supportEstablishment.value._id,
            isEngagementContacts: value?.engagementCategory?._id,
          })
          .pipe(
            map(userProfile => {
              return userProfile.data.map(x => x);
            }),

            tap(userProfile => {
              if (!userProfile.find(x => x._id === this.form.controls.engagementContact.value?._id)) {
                this.form.controls.engagementContact.setValue(null, { emitEvent: false });
                enableControlWhenAllowed(this.form, 'engagementContact', this.isUpdateDisabledPermission);
              }
            }),
          );
      }),
    );
  }

  onSave() {
    // Marquer tous les champs comme "touched" pour afficher les erreurs
    Object.keys(this.form.controls).forEach(key => {
      this.form.controls[key].markAsTouched();
    });

    // Vérifier si le formulaire est valide
    if (this.form.invalid) {
      this.snackBarService.danger('Veuillez remplir tous les champs requis');
      return;
    }

    // Si tout est valide, procéder à l'enregistrement
    this.updateStep(3);
  }

  updateStep(step: number) {
    const sub = this.contractService.updateStep(this.contract._id, { creationStep: step }).subscribe(res => {
      if (step === 3) {
        this.router.navigate(['/management/engagement/', this.contract._id, 'cga']).then();
      }
    });
    this.subscription.add(sub);
  }

  get isUserCurrentEngagementContact(): boolean {
    return (
      this.contract?.engagementContact?._id === this.authService.currentUserValue()?._id || this.authService.isPredik()
    );
  }

  private initValueChangesSubscription(): void {
    console.log('[initValueChangesSubscription] Initialisation de la subscription auto-save');

    this.subscription.add(
      this.form.valueChanges
        .pipe(
          startWith(this.form.getRawValue()), // Émettre la valeur actuelle en premier
          pairwise(), // Compare [previous, current]
          debounceTime(500),
          filter(([prev, curr]) => {
            const prevStr = JSON.stringify(prev);
            const currStr = JSON.stringify(curr);
            const hasRealChanges = prevStr !== currStr;

            console.log('[valueChanges] Changement détecté:', hasRealChanges);
            console.log('[valueChanges] prev:', prev);
            console.log('[valueChanges] curr:', curr);

            // Bypass a fix
            return true;
          }),
          map(([prev, curr]) => curr), // Ne garder que la valeur actuelle
        )
        .subscribe(formValue => {
          // Sauvegarde du statut actuel avant mise à jour
          const previousStatus = this.contract.status;

          console.log('[valueChanges] Envoi de la requête update avec:', formValue);

          // Mise à jour globale du contrat, une seule fois par changement
          this.contractService.update(this.form.getRawValue()).subscribe(res => {
            const newStatus = res.data.status;
            this.contract = res.data;

            console.log('[valueChanges] Requête update réussie, nouveau statut:', newStatus);

            // Si le statut a changé, recharger la page pour afficher le nouveau statut
            if (previousStatus !== newStatus) {
              window.location.reload();
            }
          });
        }),
    );
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.contract && this.contract?.creationStep === 1 && this.form) {
      this.form.patchValue(
        {
          _id: this.contract._id,
          genericEngagement: this.contract.genericEngagement,
          supportEstablishment: this.contract.supportEstablishment,
          object: this.contract.object,
          startValidityDate: this.contract.startValidityDate,
          endValidityDate: this.contract.endValidityDate,
          engagementContact: this.contract.engagementContact,
          noticePeriod: this.contract.noticePeriod || {
            displayedUnit: TimeUnit.DAY,
            quantity: 10080,
          },
        },
        { emitEvent: false },
      );

      if (this.contract?.supportEstablishment) {
        enableControlWhenAllowed(this.form, 'supportEstablishment', this.isUpdateDisabledPermission);
      }
      // En étape 1, les deux dates doivent toujours être modifiables
      enableControlWhenAllowed(this.form, 'startValidityDate', this.isUpdateDisabledPermission);
      enableControlWhenAllowed(this.form, 'endValidityDate', this.isUpdateDisabledPermission);

      // Réinitialise si nécessaire
      if (this.subscription.closed) {
        this.subscription = new Subscription();
      }

      this.initValueChangesSubscription();
    }

    // Gérer les changements de contrat pour les étapes après step 1
    if (changes.contract && this.contract?.creationStep !== 1 && this.form) {
      console.log('[ngOnChanges] Changement de contrat après step 1');
      console.log('[ngOnChanges] isUserCurrentEngagementContact:', this.isUserCurrentEngagementContact);

      // Activer les champs de dates si l'utilisateur est autorisé
      if (this.isUserCurrentEngagementContact) {
        console.log('[ngOnChanges] Activation des champs de dates');
        enableControlWhenAllowed(this.form, 'startValidityDate', this.isUpdateDisabledPermission);
        if (this.contract?.startValidityDate) {
          enableControlWhenAllowed(this.form, 'endValidityDate', this.isUpdateDisabledPermission);
        }
      } else {
        console.log('[ngOnChanges] Désactivation des champs de dates');
        // Sinon, désactiver les champs de dates
        this.form.controls.startValidityDate.disable({ emitEvent: false });
        this.form.controls.endValidityDate.disable({ emitEvent: false });
      }

      console.log('[ngOnChanges] startValidityDate disabled:', this.form.controls.startValidityDate.disabled);
      console.log('[ngOnChanges] endValidityDate disabled:', this.form.controls.endValidityDate.disabled);
    }
    lockFormWhenReadOnly(this.form, this.isUpdateDisabledPermission);
  }

  ngOnDestroy() {
    this.subscription.unsubscribe();
  }
}
